// C:\Apps\Bresc_Test\bresco-franquicies-frontend\src\components\ui\Table\Filters\SelectColumnFilter.js
import { useTitle } from 'contexts/TitleContext'
import PropTypes from 'prop-types'
import { useEffect, useMemo, useRef, useState } from 'react'

export default function SelectColumnFilter({ column }) {
  const { title } = useTitle()
  const { filterValue, setFilter, preFilteredRows, id } = column
  const [open, setOpen] = useState(false)
  const dropdownRef = useRef(null)
  const lastTitleRef = useRef(null)

  const selected = useMemo(
    () => (Array.isArray(filterValue) ? filterValue : []),
    [filterValue]
  )

  // Cerrar al hacer click fuera
  useEffect(() => {
    if (!open) return
    const handler = (e) => {
      if (dropdownRef.current && !dropdownRef.current.contains(e.target)) {
        setOpen(false)
      }
    }
    document.addEventListener('mousedown', handler, true)
    return () => document.removeEventListener('mousedown', handler, true)
  }, [open])

  // Persistir en localStorage como JSON
  useEffect(() => {
    const v = selected.length > 0 ? JSON.stringify(selected) : ''
    localStorage.setItem(column.id, v)
  }, [column.id, selected])

  // Rehidratar al cambiar de vista/título
  useEffect(() => {
    if (lastTitleRef.current === title) return
    lastTitleRef.current = title
    const storedRaw = localStorage.getItem(column.id)
    if (!storedRaw) return
    try {
      const stored = JSON.parse(storedRaw)
      if (Array.isArray(stored) && stored.length > 0) {
        setFilter(stored)
      }
    } catch {
      if (storedRaw !== '') setFilter([storedRaw])
    }
  }, [title, column.id, setFilter])

  // Opciones únicas ordenadas
  // const options = useMemo(() => {
  //   const s = new Set()
  //   preFilteredRows?.forEach((row) => {
  //     const v = row?.values?.[id]
  //     const value = (v ?? '').toString().trim()
  //     if (value !== '') s.add(value)
  //   })
  //   return Array.from(s).sort((a, b) =>
  //     a.localeCompare(b, undefined, { sensitivity: 'base' })
  //   )
  // }, [id, preFilteredRows])
  // DESPUÉS
  const emptyKey = column.emptyKey || null

  const { options, hasEmpty } = useMemo(() => {
    const s = new Set()
    let foundEmpty = false
    preFilteredRows?.forEach((row) => {
      const v = row?.values?.[id]
      const value = (v ?? '').toString().trim()
      if (value !== '') s.add(value)
      else foundEmpty = true
    })
    return {
      options: Array.from(s).sort((a, b) =>
        a.localeCompare(b, undefined, { sensitivity: 'base' })
      ),
      hasEmpty: foundEmpty,
    }
  }, [id, preFilteredRows])

  const toggle = (opt) => {
    const next = selected.includes(opt)
      ? selected.filter((s) => s !== opt)
      : [...selected, opt]
    setFilter(next.length > 0 ? next : undefined)
  }

  const clearAll = () => setFilter(undefined)

  const label =
    selected.length === 0
      ? 'Todos'
      // : selected.length === 1
      // ? selected[0]
      // : `${selected.length} seleccionados`
    // DESPUÉS
      : selected.length === 1
      ? selected[0] === emptyKey ? '(Vacíos)' : selected[0]
      : `${selected.length} seleccionados`
      
  const stopAll = (e) => {
    e.stopPropagation()
    e.nativeEvent?.stopImmediatePropagation?.()
  }

  return (
    <div ref={dropdownRef} className="relative print:hidden">
      {/* Botón trigger */}
      <button
        type="button"
        className="w-full py-0.5 px-2 text-xs font-normal text-left bg-white text-gray-700 border border-gray-200 rounded-sm hover:border-gray-300 focus:outline-none flex items-center justify-between gap-1 dark:bg-gray-800 dark:text-gray-300 dark:border-gray-600"
        onMouseDown={stopAll}
        onPointerDown={stopAll}
        onClick={(e) => {
          stopAll(e)
          setOpen((o) => !o)
        }}
      >
        <span className="truncate">{label}</span>
        <svg
          className={`w-3 h-3 flex-shrink-0 transition-transform ${open ? 'rotate-180' : ''}`}
          fill="none"
          stroke="currentColor"
          viewBox="0 0 24 24"
        >
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
        </svg>
      </button>

      {/* Dropdown */}
      {open && (
        <div
          className="absolute z-50 mt-1 w-max min-w-full max-w-xs bg-white border border-gray-200 rounded-sm shadow-lg max-h-60 overflow-y-auto dark:bg-gray-800 dark:border-gray-600"
          style={{ left: 0 }}
          onMouseDown={stopAll}
          onPointerDown={stopAll}
          onClick={stopAll}
        >
          {/* Opción "Todos" */}
          <label className="flex items-center gap-2 px-2 py-1 hover:bg-gray-100 cursor-pointer text-xs text-gray-700 dark:text-gray-300 dark:hover:bg-gray-700">
            <input
              type="checkbox"
              checked={selected.length === 0}
              onChange={clearAll}
              className="accent-[#FCAE52]"
            />
            <span>Todos</span>
          </label>
          <div className="border-t border-gray-200 dark:border-gray-600" />
          {/* ← AQUÍ, entre el divider de "Todos" y el options.map */}
          {emptyKey && hasEmpty && (
            <>
              <label className="flex items-center gap-2 px-2 py-1 hover:bg-gray-100 cursor-pointer text-xs text-gray-400 italic dark:text-gray-400 dark:hover:bg-gray-700">
                <input
                  type="checkbox"
                  checked={selected.includes(emptyKey)}
                  onChange={() => toggle(emptyKey)}
                  className="accent-[#FCAE52]"
                />
                <span>(Vacíos)</span>
              </label>
              <div className="border-t border-gray-200 dark:border-gray-600" />
            </>
          )}
          {options.map((opt) => (
            <label
              key={opt}
              className="flex items-center gap-2 px-2 py-1 hover:bg-gray-100 cursor-pointer text-xs text-gray-700 dark:text-gray-300 dark:hover:bg-gray-700"
            >
              <input
                type="checkbox"
                checked={selected.includes(opt)}
                onChange={() => toggle(opt)}
                className="accent-[#FCAE52]"
              />
              <span className="break-words">{opt}</span>
            </label>
          ))}
        </div>
      )}
    </div>
  )
}

SelectColumnFilter.propTypes = {
  column: PropTypes.object.isRequired,
}